import { every, findIndex, isEmpty } from 'lodash';

import * as types from './constants';
import { sorts } from '../../../pages/MyCreators/routes/MyCreatorsList/components/ConnectedTab/sort';
import { generateQueryFromString, removeFromQueryString } from '../../../utils/queryString';
import queryString from 'query-string';

export const initialState = {
  dataTable: [],
  allSelected: false,
  loading: false,
  count: 0,
  next: null,
  previous: null,
  page_url: null,
  loaded: false,
  errors_api: {},
  queryParsed: false,
  message: null,
  filters: []
};

function dedupeByIdKeepOrder(list) {
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const id = item?.id || item?.key;
    if (!id) {
      out.push(item);
      continue;
    }
    if (!seen.has(id)) {
      seen.add(id);
      out.push(item);
    }
  }
  return out;
}

const actions = {
  // Get single page
  [types.GET_CODES_COLLECTIONS_SCROLL_REQUEST]: (state, { payload }) => ({
    ...state,
    loading: true,
    errors_api: {},
    ...(!payload?.pageUrl && { loaded: false })
  }),

  // A follow-up page (`append`) extends the list, a first page replaces it.
  [types.GET_CODES_COLLECTIONS_SCROLL_DONE]: (state, { payload }) => {
    const incoming = payload.dataTable || [];

    return {
      ...state,
      loading: false,
      loaded: true,
      dataTable: payload.append ? dedupeByIdKeepOrder([...(state.dataTable || []), ...incoming]) : incoming,
      count: payload.count,
      next: payload.next,
      previous: payload.previous,
      message: payload.message
    };
  },

  [types.GET_CODES_COLLECTIONS_SCROLL_FAILED]: (state, { payload }) => ({
    ...state,
    loading: false,
    errors_api: payload
  }),

  [types.GET_CODES_COLLECTIONS_SCROLL_RESET]: () => ({ ...initialState }),

  // Optimistic
  [types.CODES_COLLECTIONS_SCROLL_OPTIMISTIC_REMOVE]: (state, { payload }) => {
    const uuid = payload;
    return {
      ...state,
      dataTable: (state.dataTable || []).filter(x => (x.uuid || x.creator?.uuid) !== uuid),
      count: Math.max(0, (state.count || 0) - 1)
    };
  },

  [types.CODES_COLLECTIONS_SCROLL_OPTIMISTIC_ADD]: (state, { payload }) => {
    const row = payload;
    const id = row?.uuid || row?.creator?.uuid || row?.key;
    if (!id) return state;

    const exists = (state.dataTable || []).some(x => (x.uuid || x.creator?.uuid || x.key) === id);
    if (exists) return state;

    return {
      ...state,
      dataTable: [row, ...(state.dataTable || [])],
      count: (state.count || 0) + 1
    };
  },

  // Operations
  [types.TOGGLE_CODES_COLLECTIONS_SCROLL]: (state, { payload }) => {
    const newDataTable = state.dataTable.map(el => el.select.id === payload ? {
      ...el,
      select: { ...el.select, value: !el.select.value }
    } : el);

    const status = every(newDataTable, x => x.select.value);

    return {
      ...state,
      allSelected: status,
      dataTable: newDataTable
    };
  },

  [types.TOGGLE_ALL_CODES_COLLECTIONS_SCROLL]: (state, { payload }) => {
    if (payload && payload.reset) {
      return {
        ...state,
        allSelected: false,
        dataTable: state.dataTable.map(el => ({
          ...el,
          select: { ...el.select, value: false }
        }))
      };
    } else {
      const status = every(state.dataTable, x => x.select.value);

      return {
        ...state,
        allSelected: !status,
        dataTable: state.dataTable.map(el => ({
          ...el,
          select: { ...el.select, value: !status }
        }))
      };
    }
  },

  [types.SET_CODES_COLLECTIONS_SCROLL_FILTER]: (state, { payload }) => {
    const index = findIndex(state.filters, x => x.key === payload.key);
    const updatedFilters = [...state.filters];
    if (index !== -1) {
      updatedFilters[index].value = payload.value;
      if (typeof updatedFilters[index].value === 'string' || Array.isArray(updatedFilters[index].value)) {
        updatedFilters[index].selected = !isEmpty(updatedFilters[index].value);
      } else {
        updatedFilters[index].selected = !isEmpty(updatedFilters[index].value.type);
      }
    }

    return {
      ...state,
      filters: updatedFilters,
      curr_page: 1
    };
  },

  [types.SET_CODES_COLLECTIONS_SCROLL_ALL_FILTERS]: (state, { payload }) => {
    let updatedFilters = [...state.filters];

    payload.forEach(filter => {
      const index = findIndex(updatedFilters, x => x.key === filter.key);
      if (index !== -1) updatedFilters[index] = { ...updatedFilters[index], ...filter, selected: true };
    });

    return {
      ...state,
      filters: updatedFilters,
      curr_page: 1,
      queryParsed: true
    };
  },

  [types.CLEAR_CODES_COLLECTIONS_SCROLL_FILTERS]: (state, { payload: { filterToUpdate, mobile } }) => {
    if (!isEmpty(filterToUpdate)) {
      const index = findIndex(state.filters, x => x.key === filterToUpdate.key);
      const updatedFilters = [...state.filters];
      if (index !== -1) {
        updatedFilters[index].value = filterToUpdate.value;
        updatedFilters[index].selected = filterToUpdate.selected;
      }

      if (!mobile) window.history.replaceState({}, '', `${window.location.pathname}${removeFromQueryString(filterToUpdate.key)}`);

      return {
        ...state,
        filters: updatedFilters,
        curr_page: 1
      };
    } else {
      const query = generateQueryFromString(window.location.search);

      if (query.tab) {
        if (!mobile) window.history.replaceState({}, '', `${window.location.pathname}?${queryString.stringify({ tab: query.tab })}`);
      } else {
        if (!mobile) window.history.replaceState({}, '', `${window.location.pathname}`);
      }

      return {
        ...state,
        filters: [
          { key: 'sort', filterTitle: 'Sort By', selected: true, value: sorts.default }
        ],
        curr_page: 1
      };
    }
  },

  [types.CODES_COLLECTIONS_SCROLL_SET_PAGE_URL]: (state, { payload }) => ({
    ...state,
    page_url: payload || null
  })
};

export default (state = initialState, action) => {
  if (actions[action.type]) return actions[action.type](state, action);
  return state;
};
